/**
 * ForcePasswordChangeGate — Full-screen blocker shown to users whose account
 * was created or password-reset by an admin. Renders ONLY after the KVKK gate
 * has been satisfied (so users see a clear order: KVKK → password change → app).
 *
 * Calls POST /api/auth/change-password with the temp password as "current".
 */
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, KeyRound, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

export default function ForcePasswordChangeGate() {
  const { user, updateUser, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Skip gate unless backend says it's required.
  // Also skip until KVKK is accepted so modals don't stack visually.
  const needsChange =
    user &&
    user.must_change_password === true &&
    user.kvkk_in_app_consent !== false; // KVKK gate has priority

  if (!needsChange) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('Yeni şifre en az 8 karakter olmalı.');
      return;
    }
    if (next !== confirm) {
      toast.error('Şifreler eşleşmiyor.');
      return;
    }
    if (next === current) {
      toast.error('Yeni şifre eski şifreyle aynı olamaz.');
      return;
    }
    setSubmitting(true);
    try {
      await fetchWithAuth('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      updateUser({ must_change_password: false });
      toast.success('Şifren güncellendi. Hoş geldin!');
    } catch (err) {
      toast.error(err?.message || 'Şifre güncellenemedi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[210] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      data-testid="force-password-change-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fpc-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="fpc-title"
              className="text-lg font-bold text-slate-800"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Şifreni Belirle
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Devam edebilmek için sana iletilen geçici şifreyi yenisiyle değiştirmen
              gerekir.
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
          <div>
            <Label htmlFor="fpc-current">Geçici şifre (size iletilen)</Label>
            <div className="relative">
              <Input
                id="fpc-current"
                type={show ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoFocus
                className="pr-10"
                data-testid="fpc-current-input"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-slate-100"
                aria-label={show ? 'Gizle' : 'Göster'}
                data-testid="fpc-toggle-visibility"
              >
                {show ? (
                  <EyeOff className="w-4 h-4 text-slate-500" />
                ) : (
                  <Eye className="w-4 h-4 text-slate-500" />
                )}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="fpc-next">Yeni şifre <span className="text-slate-400">(min 8 karakter)</span></Label>
            <Input
              id="fpc-next"
              type={show ? 'text' : 'password'}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              data-testid="fpc-new-input"
            />
          </div>

          <div>
            <Label htmlFor="fpc-confirm">Yeni şifre (tekrar)</Label>
            <Input
              id="fpc-confirm"
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              data-testid="fpc-confirm-input"
            />
            {confirm && confirm !== next && (
              <p className="text-xs text-rose-600 mt-1">Şifreler eşleşmiyor</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={logout}
              disabled={submitting}
              className="flex-1"
              data-testid="fpc-logout-btn"
            >
              Çıkış yap
            </Button>
            <Button
              type="submit"
              disabled={
                submitting || !current || next.length < 8 || next !== confirm
              }
              className="flex-1 bg-[#5A67D8] hover:bg-[#4338CA] text-white"
              data-testid="fpc-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Şifreyi belirle
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
